/**
 * TP 链上充值 — admin settings (min USDT, enabled) + deposit wallet CRUD.
 */
import { useState, useEffect } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Modal,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { DataTable } from '../../components/DataTable';
import { api, ApiError } from '../../api/client';
import { useAuthStore } from '../../store/auth';

interface WalletRow {
  id: number;
  name: string;
  address: string;
  currency: string;
  chain_id: string;
  is_active: boolean;
  sort: number;
  create_time: string;
  update_time: string;
}

interface TpRechargeSettings {
  enabled: boolean;
  min_tp: string;
  min_usdt: string;
  exchange_rate: string;
  auto_audit: boolean;
  rate: string;
  asset: 'tp';
}

const CHAIN_OPTIONS = [
  { label: 'ERC-20 (Ethereum)', value: 'ERC20' },
  { label: 'BSC (BEP-20)', value: 'BSC' },
  { label: 'TRC-20', value: 'TRC20' },
];

const CURRENCY_OPTIONS = [{ label: 'USDT', value: 'USDT' }];

export function RechargeWalletPage() {
  const qc = useQueryClient();
  const hasNode = useAuthStore((s) => s.hasNode);
  const canEdit = hasNode('recharge_wallet.update');

  const [createOpen, setCreateOpen] = useState(false);
  const [editRow, setEditRow] = useState<WalletRow | null>(null);
  const [loading, setLoading] = useState(false);

  const [createForm] = Form.useForm();
  const [editForm] = Form.useForm();
  const [settingsForm] = Form.useForm();

  const settingsQuery = useQuery({
    queryKey: ['admin', 'tp-recharge', 'settings'],
    queryFn: () => api.get<TpRechargeSettings>('/api/admin/tp-recharge/settings'),
  });

  const statsQuery = useQuery({
    queryKey: ['admin', 'tp-recharge', 'stats'],
    queryFn: () =>
      api.get<{
        online_tp: string;
        online_count: number;
        admin_tp: string;
        admin_count: number;
        total_tp: string;
      }>('/api/admin/tp-recharge/stats'),
  });

  useEffect(() => {
    if (settingsQuery.data) {
      settingsForm.setFieldsValue({
        enabled: settingsQuery.data.enabled,
        min_tp: settingsQuery.data.min_tp,
        exchange_rate: settingsQuery.data.exchange_rate,
        auto_audit: settingsQuery.data.auto_audit,
      });
    }
  }, [settingsQuery.data, settingsForm]);

  const saveSettings = useMutation({
    mutationFn: (body: Partial<TpRechargeSettings>) =>
      api.put<TpRechargeSettings>('/api/admin/tp-recharge/settings', body),
    onSuccess: (data) => {
      message.success('TP 充值设置已保存');
      settingsForm.setFieldsValue({
        enabled: data.enabled,
        min_tp: data.min_tp,
        exchange_rate: data.exchange_rate,
        auto_audit: data.auto_audit,
      });
      qc.setQueryData(['admin', 'tp-recharge', 'settings'], data);
    },
    onError: (e) => message.error(e instanceof ApiError ? e.message : '保存失败'),
  });

  const handleCreate = async (values: Record<string, unknown>) => {
    setLoading(true);
    try {
      await api.post('/api/admin/recharge-wallets', {
        name: values.name,
        address: values.address,
        currency: values.currency ?? 'USDT',
        chain_id: values.chain_id ?? 'BSC',
        is_active: values.is_active ?? true,
        sort: values.sort ?? 0,
      });
      message.success('充值钱包已添加');
      setCreateOpen(false);
      createForm.resetFields();
      qc.invalidateQueries({ queryKey: ['admin', 'recharge-wallets'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (values: Record<string, unknown>) => {
    if (!editRow) return;
    setLoading(true);
    try {
      const patch: Record<string, unknown> = {};
      if (values.name !== undefined) patch.name = values.name;
      if (values.address !== undefined) patch.address = values.address;
      if (values.currency !== undefined) patch.currency = values.currency;
      if (values.chain_id !== undefined) patch.chain_id = values.chain_id;
      if (values.is_active !== undefined) patch.is_active = values.is_active;
      if (values.sort !== undefined) patch.sort = values.sort;
      await api.patch(`/api/admin/recharge-wallets/${editRow.id}`, patch);
      message.success('钱包已更新');
      setEditRow(null);
      qc.invalidateQueries({ queryKey: ['admin', 'recharge-wallets'] });
    } catch (e) {
      message.error(e instanceof ApiError ? e.message : 'Failed');
    } finally {
      setLoading(false);
    }
  };

  const columns = [
    { title: 'ID', dataIndex: 'id', width: 60 },
    { title: '名称', dataIndex: 'name', width: 120 },
    {
      title: '地址',
      dataIndex: 'address',
      ellipsis: true,
      render: (v: string) => <span className="font-mono text-xs">{v}</span>,
    },
    {
      title: '币种',
      dataIndex: 'currency',
      width: 80,
      render: (v: string) => <Tag color="blue">{v}</Tag>,
    },
    {
      title: '链',
      dataIndex: 'chain_id',
      width: 100,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: '启用',
      dataIndex: 'is_active',
      width: 80,
      render: (v: boolean) => (v ? <Tag color="green">是</Tag> : <Tag color="red">否</Tag>),
    },
    { title: '排序', dataIndex: 'sort', width: 60 },
    {
      title: '创建时间',
      dataIndex: 'create_time',
      width: 160,
      render: (v: string) => v?.slice(0, 19).replace('T', ' '),
    },
    ...(canEdit
      ? [
          {
            title: '操作',
            width: 80,
            render: (_: unknown, row: WalletRow) => (
              <Button
                size="small"
                onClick={() => {
                  setEditRow(row);
                  editForm.setFieldsValue({
                    name: row.name,
                    address: row.address,
                    currency: row.currency,
                    chain_id: row.chain_id,
                    is_active: row.is_active,
                    sort: row.sort,
                  });
                }}
              >
                编辑
              </Button>
            ),
          },
        ]
      : []),
  ];

  return (
    <>
      <Typography.Title level={4} style={{ marginTop: 0 }}>
        TP 链上充值
      </Typography.Title>
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="用户连接已绑定的 BEP20 钱包向下方地址转出 USDT；链上确认后按汇率入账 TP（1 TP = exchange_rate USDT）。最低 500 TP，整 500 倍数。"
      />

      <Card title="充值统计" loading={statsQuery.isLoading} style={{ marginBottom: 16 }}>
        <Space size="large" wrap>
          <div>
            <Typography.Text type="secondary">在线充值合计（TP）</Typography.Text>
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {statsQuery.data?.online_tp ?? '—'}
              <Typography.Text type="secondary" style={{ fontSize: 13, marginLeft: 8 }}>
                {statsQuery.data ? `${statsQuery.data.online_count} 笔` : ''}
              </Typography.Text>
            </div>
          </div>
          <div>
            <Typography.Text type="secondary">后台敲入合计（TP）</Typography.Text>
            <div style={{ fontSize: 20, fontWeight: 600 }}>
              {statsQuery.data?.admin_tp ?? '—'}
              <Typography.Text type="secondary" style={{ fontSize: 13, marginLeft: 8 }}>
                {statsQuery.data ? `${statsQuery.data.admin_count} 笔` : ''}
              </Typography.Text>
            </div>
          </div>
          <div>
            <Typography.Text type="secondary">总计（TP）</Typography.Text>
            <div style={{ fontSize: 20, fontWeight: 600 }}>{statsQuery.data?.total_tp ?? '—'}</div>
          </div>
        </Space>
      </Card>

      <Card
        title="充值规则"
        loading={settingsQuery.isLoading}
        style={{ marginBottom: 16 }}
        extra={
          canEdit ? (
            <Button
              type="primary"
              loading={saveSettings.isPending}
              onClick={() => settingsForm.submit()}
            >
              保存设置
            </Button>
          ) : null
        }
      >
        <Form
          form={settingsForm}
          layout="vertical"
          disabled={!canEdit}
          onFinish={(v) => saveSettings.mutate(v)}
        >
          <Form.Item name="enabled" label="开放链上充值" valuePropName="checked">
            <Switch checkedChildren="开" unCheckedChildren="关" />
          </Form.Item>
          <Form.Item
            name="min_tp"
            label="最低充值 (TP)"
            rules={[{ required: true, message: '请输入最低 TP' }]}
          >
            <InputNumber min={100} step={100} style={{ width: 200 }} addonAfter="TP" />
          </Form.Item>
          <Form.Item
            name="exchange_rate"
            label="TP 充值汇率 (USDT / 1 TP)"
            rules={[{ required: true, message: '请输入汇率' }]}
          >
            <InputNumber min={0.01} step={0.01} style={{ width: 200 }} addonAfter="USDT/TP" />
          </Form.Item>
          <Form.Item name="auto_audit" label="链上验证后自动入账 TP" valuePropName="checked">
            <Switch checkedChildren="自动" unCheckedChildren="人工审核" />
          </Form.Item>
          <Typography.Text type="secondary">
            例：500 TP × 1.14 = 570 USDT 应付
          </Typography.Text>
        </Form>
      </Card>

      <Card title="平台收款钱包（固定地址）" style={{ marginBottom: 16 }}>
        <Space style={{ marginBottom: 16 }}>
          {hasNode('recharge_wallet.create') && (
            <Button type="primary" onClick={() => setCreateOpen(true)}>
              添加钱包
            </Button>
          )}
        </Space>
        <Typography.Paragraph type="secondary" style={{ marginBottom: 12 }}>
          用户 USDT 转入以下地址。BSC 链请配置 <Tag>BSC</Tag>；以太坊请配置 <Tag>ERC20</Tag>。同链多条时按排序取第一条。
        </Typography.Paragraph>
        <DataTable<WalletRow>
          queryKey={['admin', 'recharge-wallets']}
          endpoint="/api/admin/recharge-wallets"
          columns={columns}
          rowKey="id"
        />
      </Card>

      <Modal
        title="添加收款钱包"
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onOk={() => createForm.submit()}
        confirmLoading={loading}
      >
        <Form form={createForm} layout="vertical" onFinish={handleCreate}>
          <Form.Item name="name" label="显示名称" rules={[{ required: true }]}>
            <Input maxLength={100} placeholder="例如：BSC 主收款" />
          </Form.Item>
          <Form.Item name="address" label="钱包地址 (0x…)" rules={[{ required: true }]}>
            <Input maxLength={255} placeholder="0x..." />
          </Form.Item>
          <Form.Item name="currency" label="币种" initialValue="USDT">
            <Select options={CURRENCY_OPTIONS} />
          </Form.Item>
          <Form.Item name="chain_id" label="链" initialValue="BSC">
            <Select options={CHAIN_OPTIONS} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked" initialValue={true}>
            <Switch />
          </Form.Item>
          <Form.Item name="sort" label="排序（越小越优先）" initialValue={0}>
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>

      <Modal
        title={`编辑钱包 #${editRow?.id}`}
        open={!!editRow}
        onCancel={() => setEditRow(null)}
        onOk={() => editForm.submit()}
        confirmLoading={loading}
      >
        <Form form={editForm} layout="vertical" onFinish={handleEdit}>
          <Form.Item name="name" label="显示名称">
            <Input maxLength={100} />
          </Form.Item>
          <Form.Item name="address" label="钱包地址">
            <Input maxLength={255} />
          </Form.Item>
          <Form.Item name="currency" label="币种">
            <Select options={CURRENCY_OPTIONS} />
          </Form.Item>
          <Form.Item name="chain_id" label="链">
            <Select options={CHAIN_OPTIONS} />
          </Form.Item>
          <Form.Item name="is_active" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="sort" label="排序">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
}
